/**
 * Voucher Instances Service
 * Handles fetching and filtering individual voucher instances (both API and UI created)
 */

import { supabase } from "./supabase";

export interface VoucherInstance {
  id: string;
  voucher_code: string;
  voucher_name: string;
  membership_id: string;
  email: string;
  discount_value: number;
  event_code?: string;
  is_redeemed: boolean;
  redeemed_at?: string;
  redeemed_by?: string;
  created_at: string;
  start_date?: number;
  end_date?: number;
  template_id?: string;
  qr_image_url?: string;

  // Joined data
  template?: VoucherTemplateInfo;
}

// Shape of template data from Supabase join
interface VoucherTemplateInfo {
  voucher_type: string;
  voucher_promo_code: string;
  states: string[];
}

// Raw row shape from Supabase query with joined template
interface VoucherRowWithTemplate {
  id: string;
  voucher_code: string;
  voucher_name: string;
  membership_id: string;
  email: string;
  discount_value: number;
  event_code?: string;
  is_redeemed: boolean;
  redeemed_at?: string;
  redeemed_by?: string;
  created_at: string;
  start_date?: number;
  end_date?: number;
  template_id?: string;
  qr_image_url?: string;
  voucher_templates: VoucherTemplateInfo | null;
}

export interface VoucherInstanceFilters {
  search?: string; // Search by voucher name, code, email, membership_id
  voucher_type?: string[]; // Filter by voucher type (from template)
  states?: string[]; // Filter by state
  organization_ids?: string[]; // Filter by organization or location
  status?: "redeemed" | "not_redeemed" | "all";
  date_from?: string; // ISO date string
  date_to?: string; // ISO date string
  sort_by?: "date" | "voucher_type" | "organization";
  sort_order?: "asc" | "desc";
  limit?: number;
  offset?: number;
}

/**
 * Get voucher instances with filtering and pagination
 */
export async function getVoucherInstances(
  filters: VoucherInstanceFilters = {},
): Promise<{ data: VoucherInstance[]; total: number }> {
  try {
    const {
      search,
      voucher_type,
      states,
      organization_ids: _organization_ids,
      status = "all",
      date_from,
      date_to,
      sort_by = "date",
      sort_order = "desc",
      limit = 50,
      offset = 0,
    } = filters;

    // Build query - join with template to get type, promo code, and states
    let query = supabase.from("vouchers").select(
      `
        id,
        voucher_code,
        voucher_name,
        membership_id,
        email,
        discount_value,
        event_code,
        is_redeemed,
        redeemed_at,
        redeemed_by,
        created_at,
        start_date,
        end_date,
        template_id,
        qr_image_url,
        voucher_templates (
          voucher_type,
          voucher_promo_code,
          states
        )
      `,
      { count: "exact" },
    );

    // Non-monetary rewards (e.g. Event Drawing entries) are $0 and are not real vouchers — keep them out
    // of the voucher-instances list + count.
    query = query.not("is_non_monetary", "is", true);
    // $0 event-ticket placeholders (token-linked, no real value) are admissions, not vouchers —
    // hidden here the same way (ticket visibility lives at /admin/ticket-rewards). The SAME row
    // reappears naturally once check-in escalation assigns real value. Canonical rule (audit
    // iteration 1): visible iff token_id IS NULL OR coalesce(applied_amount, discount_value) > 0.
    query = query.or(
      "token_id.is.null,and(applied_amount.is.null,discount_value.gt.0),applied_amount.gt.0",
    );

    // Search filter
    if (search && search.trim()) {
      query = query.or(
        `voucher_name.ilike.*${search}*,voucher_code.ilike.*${search}*,email.ilike.*${search}*,membership_id.ilike.*${search}*`,
      );
    }

    // Status filter
    if (status === "redeemed") {
      query = query.eq("is_redeemed", true);
    } else if (status === "not_redeemed") {
      query = query.eq("is_redeemed", false);
    }

    // Date range filter (using created_at)
    if (date_from) {
      query = query.gte("created_at", date_from);
    }
    if (date_to) {
      // Add one day to include the entire end date
      const endDate = new Date(date_to);
      endDate.setDate(endDate.getDate() + 1);
      query = query.lt("created_at", endDate.toISOString());
    }

    // Sorting
    if (sort_by === "date") {
      query = query.order("created_at", { ascending: sort_order === "asc" });
    } else if (sort_by === "voucher_type") {
      // Sort by template type, nulls last
      query = query.order("template_id", {
        ascending: sort_order === "asc",
        nullsFirst: false,
      });
    }

    // Pagination
    query = query.range(offset, offset + limit - 1);

    const { data, error, count } = await query;

    if (error) {
      console.error("Error fetching voucher instances:", error);
      throw new Error("Failed to fetch voucher instances");
    }

    // Post-process filters that can't be done in SQL
    const rawData = (data || []) as VoucherRowWithTemplate[];

    // Filter by voucher type (from template)
    let filteredData = rawData;
    if (voucher_type && voucher_type.length > 0) {
      filteredData = filteredData.filter((v) => {
        if (!v.voucher_templates) {
          return false;
        }
        return voucher_type.includes(v.voucher_templates.voucher_type);
      });
    }

    // Filter by states (from template)
    if (states && states.length > 0) {
      filteredData = filteredData.filter((v) => {
        if (!v.voucher_templates) {
          return true;
        } // Include if no template
        const templateStates = v.voucher_templates.states || [];
        if (templateStates.length === 0) {
          return true;
        } // Include if template has no state restrictions
        return states.some((state) => templateStates.includes(state));
      });
    }

    // Transform data to flatten template
    const transformedData: VoucherInstance[] = filteredData.map((v) => ({
      id: v.id,
      voucher_code: v.voucher_code,
      voucher_name: v.voucher_name,
      membership_id: v.membership_id,
      email: v.email,
      discount_value: v.discount_value,
      event_code: v.event_code,
      is_redeemed: v.is_redeemed,
      redeemed_at: v.redeemed_at,
      redeemed_by: v.redeemed_by,
      created_at: v.created_at,
      start_date: v.start_date,
      end_date: v.end_date,
      template_id: v.template_id,
      qr_image_url: v.qr_image_url,
      template: v.voucher_templates
        ? {
            voucher_type: v.voucher_templates.voucher_type,
            voucher_promo_code: v.voucher_templates.voucher_promo_code,
            states: v.voucher_templates.states || [],
          }
        : undefined,
    }));

    return {
      data: transformedData,
      total: count || 0,
    };
  } catch (error) {
    console.error("Error in getVoucherInstances:", error);
    return { data: [], total: 0 };
  }
}

/**
 * Get unique values for filter dropdowns
 */
export async function getVoucherFilterOptions(): Promise<{
  voucher_types: string[];
  states: string[];
  events: string[];
}> {
  try {
    // Get unique voucher types from templates
    const { data: templates } = await supabase
      .from("voucher_templates")
      .select("voucher_type")
      .is("deleted_at", null);

    const voucher_types = Array.from(
      new Set(templates?.map((t) => t.voucher_type) || []),
    );

    // Get unique states from templates
    const { data: templatesWithStates } = await supabase
      .from("voucher_templates")
      .select("states")
      .is("deleted_at", null);

    const allStates = templatesWithStates?.flatMap((t) => t.states || []) || [];
    const states = Array.from(new Set(allStates));

    // Get unique event codes from vouchers
    const { data: vouchers } = await supabase
      .from("vouchers")
      .select("event_code")
      .not("event_code", "is", null);

    const events = Array.from(
      new Set(vouchers?.map((v) => v.event_code).filter(Boolean) || []),
    );

    return {
      voucher_types,
      states,
      events,
    };
  } catch (error) {
    console.error("Error fetching filter options:", error);
    return {
      voucher_types: [],
      states: [],
      events: [],
    };
  }
}

/**
 * Daily analytics data for vouchers
 */
export interface VoucherDailyAnalytics {
  date: string; // YYYY-MM-DD format
  issued_count: number;
  issued_value: number;
  redeemed_count: number;
  redeemed_value: number;
}

/**
 * Get daily voucher analytics (issued vs redeemed) for the given filters
 * Returns data grouped by day showing total value issued and redeemed
 */
export async function getVoucherDailyAnalytics(
  filters: VoucherInstanceFilters = {},
): Promise<VoucherDailyAnalytics[]> {
  try {
    const {
      voucher_type,
      states,
      organization_ids: _organization_ids,
      status,
      date_from,
      date_to,
    } = filters;

    // Build base query to get all vouchers matching the filters
    let query = supabase.from("vouchers").select(`
        id,
        created_at,
        discount_value,
        is_redeemed,
        redeemed_at,
        voucher_templates (
          voucher_type,
          states
        )
      `);

    // Exclude $0 non-monetary rewards (Event Drawing entries) from issued/redeemed daily counts.
    query = query.not("is_non_monetary", "is", true);
    // $0 event-ticket placeholders too — MUST match getVoucherInstances or this chart contradicts
    // the list rendered on the same tab (audit iteration 1, MEDIUM-HIGH).
    query = query.or(
      "token_id.is.null,and(applied_amount.is.null,discount_value.gt.0),applied_amount.gt.0",
    );

    // Apply same filters as main list
    if (status === "redeemed") {
      query = query.eq("is_redeemed", true);
    } else if (status === "not_redeemed") {
      query = query.eq("is_redeemed", false);
    }

    // Date range filter
    if (date_from) {
      query = query.gte("created_at", date_from);
    }
    if (date_to) {
      const endDate = new Date(date_to);
      endDate.setDate(endDate.getDate() + 1);
      query = query.lt("created_at", endDate.toISOString());
    }

    const { data, error } = await query;

    if (error) {
      console.error("Error fetching voucher analytics:", error);
      throw new Error("Failed to fetch voucher analytics");
    }

    // Type the raw data from Supabase
    interface AnalyticsRow {
      id: string;
      created_at: string;
      discount_value: number;
      is_redeemed: boolean;
      redeemed_at?: string;
      voucher_templates: VoucherTemplateInfo | null;
    }

    const rawData = (data || []) as AnalyticsRow[];

    // Post-process filters for voucher type
    let filteredData = rawData;
    if (voucher_type && voucher_type.length > 0) {
      filteredData = filteredData.filter((v) => {
        if (!v.voucher_templates) {
          return false;
        }
        return voucher_type.includes(v.voucher_templates.voucher_type);
      });
    }

    // Post-process filters for states
    if (states && states.length > 0) {
      filteredData = filteredData.filter((v) => {
        if (!v.voucher_templates) {
          return true;
        }
        const templateStates = v.voucher_templates.states || [];
        if (templateStates.length === 0) {
          return true;
        }
        return states.some((state) => templateStates.includes(state));
      });
    }

    // Group by date
    const dailyMap = new Map<string, VoucherDailyAnalytics>();

    filteredData.forEach((voucher) => {
      // Get issued date (created_at)
      const issuedDate = new Date(voucher.created_at)
        .toISOString()
        .split("T")[0];

      if (!dailyMap.has(issuedDate)) {
        dailyMap.set(issuedDate, {
          date: issuedDate,
          issued_count: 0,
          issued_value: 0,
          redeemed_count: 0,
          redeemed_value: 0,
        });
      }

      const dayData = dailyMap.get(issuedDate)!;
      dayData.issued_count += 1;
      dayData.issued_value += voucher.discount_value;

      // If redeemed, also count for redeemed date
      if (voucher.is_redeemed && voucher.redeemed_at) {
        const redeemedDate = new Date(voucher.redeemed_at)
          .toISOString()
          .split("T")[0];

        if (!dailyMap.has(redeemedDate)) {
          dailyMap.set(redeemedDate, {
            date: redeemedDate,
            issued_count: 0,
            issued_value: 0,
            redeemed_count: 0,
            redeemed_value: 0,
          });
        }

        const redeemedDayData = dailyMap.get(redeemedDate)!;
        redeemedDayData.redeemed_count += 1;
        redeemedDayData.redeemed_value += voucher.discount_value;
      }
    });

    // Convert map to array and sort by date
    const result = Array.from(dailyMap.values()).sort((a, b) =>
      a.date.localeCompare(b.date),
    );

    return result;
  } catch (error) {
    console.error("Error in getVoucherDailyAnalytics:", error);
    return [];
  }
}
